// frontend/src/pages/AdministracionCuentas.jsx
// Pantalla de superuser: crear cuentas y gestionar qué usuarios tienen acceso a cada una.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../services/authService';
import superadminService from '../services/superadminService';
import Loading from '../components/Loading';
import './AdministracionCuentas.css';

const errMsg = (e) => e?.error || e?.detail || e?.message || 'Ocurrió un error';

const AdministracionCuentas = () => {
  const currentUser = authService.getUser();
  const [cuentas, setCuentas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seleccionada, setSeleccionada] = useState(null); // { cuenta, miembros }
  const [filtro, setFiltro] = useState('');
  const [formCuenta, setFormCuenta] = useState(null); // null | {id?, nombre, cliente, empresa}
  const [showAgregar, setShowAgregar] = useState(false);
  const [showCrearUsuario, setShowCrearUsuario] = useState(false);

  const cargarCuentas = useCallback(async () => {
    const data = await superadminService.getCuentas();
    setCuentas(data.cuentas || []);
  }, []);

  const abrirCuenta = useCallback(async (id) => {
    try {
      const data = await superadminService.getCuenta(id);
      setSeleccionada({ cuenta: data.cuenta, miembros: data.miembros });
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [, t] = await Promise.all([cargarCuentas(), superadminService.getTipos()]);
        setTipos(t.tipos || []);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, [cargarCuentas]);

  if (!currentUser?.is_superuser) {
    return <div className="adm-cuentas"><p>Esta sección es solo para superusuarios.</p></div>;
  }
  if (loading) return <Loading />;

  const refrescar = async (miembros) => {
    setSeleccionada((s) => ({ ...s, miembros, cuenta: { ...s.cuenta, total_usuarios: miembros.length } }));
    await cargarCuentas();
  };

  const guardarCuenta = async (e) => {
    e.preventDefault();
    try {
      if (formCuenta.id) {
        await superadminService.actualizarCuenta(formCuenta.id, {
          nombre: formCuenta.nombre, cliente: formCuenta.cliente, empresa: formCuenta.empresa });
        toast.success('Cuenta actualizada');
        await cargarCuentas();
        await abrirCuenta(formCuenta.id);
      } else {
        const r = await superadminService.crearCuenta({
          nombre: formCuenta.nombre, cliente: formCuenta.cliente, empresa: formCuenta.empresa,
          admin_user_ids: formCuenta.agregarme ? [currentUser.id] : [] });
        toast.success('Cuenta creada');
        await cargarCuentas();
        await abrirCuenta(r.cuenta.id);
      }
      setFormCuenta(null);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const cambiarTipo = async (userId, tipoId) => {
    try {
      const r = await superadminService.cambiarTipo(seleccionada.cuenta.id, userId, tipoId);
      await refrescar(r.miembros); toast.success('Tipo actualizado');
    } catch (e) { toast.error(errMsg(e)); }
  };

  const quitar = async (m) => {
    if (!window.confirm(`¿Quitar a ${m.nombre} de "${seleccionada.cuenta.nombre}"? Perderá sus grupos y clientes en esta cuenta.`)) return;
    try {
      const r = await superadminService.quitarUsuario(seleccionada.cuenta.id, m.user_id);
      await refrescar(r.miembros); toast.success('Usuario quitado');
    } catch (e) { toast.error(errMsg(e)); }
  };

  const visibles = cuentas.filter((c) =>
    `${c.nombre} ${c.cliente || ''} ${c.empresa || ''}`.toLowerCase().includes(filtro.toLowerCase()));

  return (
    <div className="adm-cuentas">
      <div className="adm-header">
        <div>
          <h1>Administración de cuentas</h1>
          <p>Crea cuentas y define qué usuarios tienen acceso a cada una.</p>
        </div>
        <button className="adm-btn primary" onClick={() => setFormCuenta({ nombre: '', cliente: '', empresa: '', agregarme: true })}>
          + Nueva cuenta
        </button>
      </div>

      <div className="adm-layout">
        <aside className="adm-lista">
          <input className="adm-input" placeholder="Buscar cuenta…" value={filtro} onChange={(e) => setFiltro(e.target.value)} />
          {visibles.map((c) => (
            <button key={c.id} className={`adm-cuenta-item${seleccionada?.cuenta.id === c.id ? ' active' : ''}`} onClick={() => abrirCuenta(c.id)}>
              <strong>{c.nombre}</strong>
              <span>{[c.cliente, c.empresa].filter(Boolean).join(' · ') || 'Sin cliente/empresa'}</span>
              <em>{c.total_usuarios} usuario{c.total_usuarios === 1 ? '' : 's'}</em>
            </button>
          ))}
          {visibles.length === 0 && <p className="adm-vacio">No hay cuentas.</p>}
        </aside>

        <section className="adm-detalle">
          {!seleccionada ? (
            <p className="adm-vacio">Selecciona una cuenta para ver y editar sus usuarios.</p>
          ) : (
            <>
              <div className="adm-detalle-head">
                <div>
                  <h2>{seleccionada.cuenta.nombre}</h2>
                  <p>{[seleccionada.cuenta.cliente && `Cliente: ${seleccionada.cuenta.cliente}`, seleccionada.cuenta.empresa && `Empresa: ${seleccionada.cuenta.empresa}`].filter(Boolean).join(' · ') || 'Sin cliente/empresa'}</p>
                </div>
                <div className="adm-acciones">
                  <button className="adm-btn" onClick={() => setFormCuenta({ id: seleccionada.cuenta.id, nombre: seleccionada.cuenta.nombre, cliente: seleccionada.cuenta.cliente || '', empresa: seleccionada.cuenta.empresa || '' })}>Editar</button>
                  <button className="adm-btn" onClick={() => setShowAgregar(true)}>Agregar usuario existente</button>
                  <button className="adm-btn primary" onClick={() => setShowCrearUsuario(true)}>Crear usuario nuevo</button>
                </div>
              </div>

              <table className="adm-tabla">
                <thead><tr><th>Usuario</th><th>Email</th><th>Tipo en esta cuenta</th><th>Estado</th><th></th></tr></thead>
                <tbody>
                  {seleccionada.miembros.map((m) => (
                    <tr key={m.user_id}>
                      <td>{m.nombre}<small>@{m.username}{m.is_superuser ? ' · superuser' : ''}</small></td>
                      <td>{m.email}</td>
                      <td>
                        <select className="adm-input" value={m.tipo_usuario?.id || ''} onChange={(e) => e.target.value && cambiarTipo(m.user_id, parseInt(e.target.value, 10))}>
                          {!m.tipo_usuario && <option value="">Sin tipo</option>}
                          {tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                        </select>
                      </td>
                      <td>{m.is_active ? 'Activo' : 'Inactivo'}</td>
                      <td><button className="adm-btn danger" onClick={() => quitar(m)}>Quitar</button></td>
                    </tr>
                  ))}
                  {seleccionada.miembros.length === 0 && <tr><td colSpan="5" className="adm-vacio">Esta cuenta aún no tiene usuarios.</td></tr>}
                </tbody>
              </table>
              <p className="adm-nota">
                Grupos de permisos, clientes y etiquetas de cada usuario se editan en{' '}
                <Link to="/desarrolla?tab=usuarios">Gestión de usuarios</Link> (cambia a esta cuenta como superusuario).
              </p>
            </>
          )}
        </section>
      </div>

      {formCuenta && (
        <Modal titulo={formCuenta.id ? 'Editar cuenta' : 'Nueva cuenta'} onClose={() => setFormCuenta(null)}>
          <form onSubmit={guardarCuenta} className="adm-form">
            <label>Nombre de la cuenta *<input className="adm-input" required value={formCuenta.nombre} onChange={(e) => setFormCuenta({ ...formCuenta, nombre: e.target.value })} /></label>
            <label>Cliente<input className="adm-input" value={formCuenta.cliente} onChange={(e) => setFormCuenta({ ...formCuenta, cliente: e.target.value })} /></label>
            <label>Empresa<input className="adm-input" value={formCuenta.empresa} onChange={(e) => setFormCuenta({ ...formCuenta, empresa: e.target.value })} /></label>
            {!formCuenta.id && (
              <label className="adm-check"><input type="checkbox" checked={formCuenta.agregarme} onChange={(e) => setFormCuenta({ ...formCuenta, agregarme: e.target.checked })} /> Agregarme como Administrador de esta cuenta</label>
            )}
            <div className="adm-form-actions">
              <button type="button" className="adm-btn" onClick={() => setFormCuenta(null)}>Cancelar</button>
              <button type="submit" className="adm-btn primary">Guardar</button>
            </div>
          </form>
        </Modal>
      )}

      {showAgregar && (
        <AgregarExistente tipos={tipos} cuenta={seleccionada.cuenta} miembros={seleccionada.miembros}
          onClose={() => setShowAgregar(false)}
          onDone={async (miembros) => { await refrescar(miembros); setShowAgregar(false); }} />
      )}
      {showCrearUsuario && (
        <CrearUsuario tipos={tipos} cuenta={seleccionada.cuenta}
          onClose={() => setShowCrearUsuario(false)}
          onDone={async (miembros) => { await refrescar(miembros); setShowCrearUsuario(false); }} />
      )}
    </div>
  );
};

const Modal = ({ titulo, onClose, children }) => (
  <div className="adm-overlay" onClick={onClose}>
    <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
      <h3>{titulo}</h3>
      {children}
    </div>
  </div>
);

const tipoPorDefecto = (tipos) => (tipos.find((t) => t.nombre === 'Usuario') || tipos[0])?.id || '';

const AgregarExistente = ({ tipos, cuenta, miembros, onClose, onDone }) => {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [elegido, setElegido] = useState(null);
  const [tipoId, setTipoId] = useState(tipoPorDefecto(tipos));
  const ya = new Set(miembros.map((m) => m.user_id));

  useEffect(() => {
    const t = setTimeout(async () => {
      try { setResultados((await superadminService.buscarUsuarios(q)).usuarios || []); } catch (e) { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const guardar = async () => {
    try {
      const r = await superadminService.agregarUsuario(cuenta.id, elegido.id, parseInt(tipoId, 10));
      toast.success(r.message); onDone(r.miembros);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Modal titulo={`Agregar usuario a ${cuenta.nombre}`} onClose={onClose}>
      <input className="adm-input" autoFocus placeholder="Buscar por nombre, usuario o email…" value={q} onChange={(e) => { setQ(e.target.value); setElegido(null); }} />
      <ul className="adm-resultados">
        {resultados.map((u) => (
          <li key={u.id}>
            <button type="button" disabled={ya.has(u.id)} className={elegido?.id === u.id ? 'active' : ''} onClick={() => setElegido(u)}>
              {u.nombre} <small>@{u.username} · {u.email}</small>{ya.has(u.id) && <em> (ya pertenece)</em>}
            </button>
          </li>
        ))}
      </ul>
      <label>Tipo en esta cuenta
        <select className="adm-input" value={tipoId} onChange={(e) => setTipoId(e.target.value)}>
          {tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </select>
      </label>
      <div className="adm-form-actions">
        <button className="adm-btn" onClick={onClose}>Cancelar</button>
        <button className="adm-btn primary" disabled={!elegido || !tipoId} onClick={guardar}>Agregar</button>
      </div>
    </Modal>
  );
};

const CrearUsuario = ({ tipos, cuenta, onClose, onDone }) => {
  const [f, setF] = useState({ username: '', email: '', first_name: '', last_name: '', password: '', tipo_usuario_id: tipoPorDefecto(tipos) });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const guardar = async (e) => {
    e.preventDefault();
    try {
      const r = await superadminService.crearUsuario(cuenta.id, { ...f, tipo_usuario_id: parseInt(f.tipo_usuario_id, 10) });
      toast.success(r.message); onDone(r.miembros);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Modal titulo={`Nuevo usuario en ${cuenta.nombre}`} onClose={onClose}>
      <form onSubmit={guardar} className="adm-form">
        <label>Nombre<input className="adm-input" value={f.first_name} onChange={set('first_name')} /></label>
        <label>Apellido<input className="adm-input" value={f.last_name} onChange={set('last_name')} /></label>
        <label>Usuario *<input className="adm-input" required value={f.username} onChange={set('username')} /></label>
        <label>Email *<input className="adm-input" type="email" required value={f.email} onChange={set('email')} /></label>
        <label>Contraseña *<input className="adm-input" type="password" required value={f.password} onChange={set('password')} /></label>
        <label>Tipo
          <select className="adm-input" value={f.tipo_usuario_id} onChange={set('tipo_usuario_id')}>
            {tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </label>
        <div className="adm-form-actions">
          <button type="button" className="adm-btn" onClick={onClose}>Cancelar</button>
          <button type="submit" className="adm-btn primary">Crear</button>
        </div>
      </form>
    </Modal>
  );
};

export default AdministracionCuentas;
